<template>
  <b-table
    ref="tableRef"
    :fields="columns"
    :items="itemsProvider"
    :filter="search"
    :current-page="currentPage"
    :per-page="pageSize"
    :sort-by.sync="sortBy"
    :sort-desc.sync="sortDesc"
    :sort-direction="sortDirection"
    :tbody-tr-class="tbodyTrClass"
    class="mb-0"
    no-sort-reset
    responsive
    sticky-header
    show-empty
    small
    @refreshed="calcVisibleItems"
    @row-selected="(payload) => $emit('row-selected', payload)"
  >
    <template #table-busy>
      <div v-loading="true" class="text-center my-2">
        <strong>Please wait...</strong>
      </div>
    </template>

    <!-- The <col>s go straight into the slot rather than through a
         component: a component needs one root, and the vue-fragment
         <Fragment> previously used to get around that breaks Vue's
         patching (insertBefore NotFoundError) as soon as the column list
         changes at runtime, e.g. the Draft invoices Market/LIMOSS toggle. -->
    <template v-slot:table-colgroup>
      <col
        v-for="column in columns"
        :key="column.key"
        :style="column.width ? { width: `${column.width}px`, minWidth: `${column.width}px` } : {}"
      />
    </template>

    <template v-for="column in columns" v-slot:[`head(${column.key})`]="head">
      <BaseDataTableTableHead :column-name="column.key">
        <slot v-bind="head" :name="`head(${column.key})`" />

        <template v-slot:[`search(${column.key})`]="data">
          <slot v-bind="data" :name="`search(${column.key})`" />
        </template>
      </BaseDataTableTableHead>
    </template>

    <template v-for="column in columns" v-slot:[`cell(${column.key})`]="cell">
      <BaseDataTableTableCell
        :index="cell.index"
        :column="column"
        :value="cell.value"
      >
        <template v-slot:default="{ isVisible }">
          <slot v-bind="{ ...cell, isVisible }" :name="`cell(${column.key})`" />
        </template>
      </BaseDataTableTableCell>
    </template>
  </b-table>
</template>

<script>
import {
  defineComponent,
  inject,
  ref,
  provide,
  nextTick,
  watch,
  onUnmounted
} from '@vue/composition-api'
import { isArray, throttle } from 'lodash-es'
import { format } from 'date-fns'

import {
  BaseDataTableInjectionKey,
  BaseDataTableTableInjectionKey
} from './BaseDataTable.constants'
import BaseDataTableTableHead from './BaseDataTableTableHead.vue'
import BaseDataTableTableCell from './BaseDataTableTableCell.vue'

export default defineComponent({
  name: 'BaseDataTableTable',
  components: {
    BaseDataTableTableHead,
    BaseDataTableTableCell
  },
  emits: ['row-selected'],
  setup() {
    const {
      columns,
      items,
      tbodyTrClass,

      search,

      filters,

      currentPage,
      pageSize,

      sortBy,
      sortDesc,
      sortDirection
    } = inject(BaseDataTableInjectionKey)

    const tableRef = ref(null)

    const itemsProvider = async (context) => {
      const providerFilters = filters.value
        .map((filter) => {
          if (!isArray(filter.value)) return filter

          return [
            { key: `${filter.key}_gt`, value: filter.value[0] },
            { key: `${filter.key}_lt`, value: filter.value[1] }
          ]
        })
        .flat()

      const results = await items.value({
        ...context,
        filters: providerFilters
      })

      return results || []
    }

    watch(filters, () => {
      if (!tableRef.value) return

      tableRef.value.refresh()
    })

    // Visible items
    const visibleIndexes = ref([])

    const calcVisibleItems = async () => {
      await nextTick()

      const { scrollTop, clientHeight } = tableRef.value.$el || {}

      const bTableBody = tableRef.value.$el.querySelector('tbody')

      if (!bTableBody) return

      const offsetStart = scrollTop + bTableBody.offsetTop
      const offsetEnd = offsetStart + clientHeight

      const newIndexes = Array.from(bTableBody.children)
        .filter((item, index) => {
          item.index = index
          return item.offsetTop >= offsetStart && item.offsetTop <= offsetEnd
        })
        .map((item) => item.index)

      if (JSON.stringify(newIndexes) !== JSON.stringify(visibleIndexes.value)) {
        visibleIndexes.value = newIndexes
      }
    }

    const throttledCalcVisibleItems = throttle(calcVisibleItems, 50)

    let scrollHandler = null

    watch(tableRef, async (newTableRef) => {
      if (!newTableRef) return

      scrollHandler = (evt) => {
        const { scrollTop, scrollHeight, clientHeight } = evt.target

        const scrollCalc =
          scrollTop / (scrollHeight - clientHeight) >= 0.95
            ? 1
            : scrollTop / (scrollHeight - clientHeight)

        scrollPercentage.value = scrollCalc * 100
      }

      newTableRef.$el.addEventListener('scroll', scrollHandler)
    })

    onUnmounted(() => {
      if (tableRef.value && scrollHandler) {
        tableRef.value.$el.removeEventListener('scroll', scrollHandler)
      }
      throttledCalcVisibleItems.cancel()  // add this
    })

    // Scrolling
    const scrollPercentage = ref(0)

    watch(scrollPercentage, throttledCalcVisibleItems)

    provide(BaseDataTableTableInjectionKey, {
      tableRef,

      visibleIndexes
    })

    return {
      format,

      columns,
      items,
      tbodyTrClass,

      search,

      currentPage,
      pageSize,

      sortBy,
      sortDesc,
      sortDirection,

      tableRef,

      itemsProvider,

      calcVisibleItems
    }
  }
})
</script>

<style scoped>
.b-table-sticky-header {
  max-height: 550px;
}

.b-table-sticky-header >>> .b-table th {
  padding: 8px 16px;
  border-top: 0;
  border-bottom: 0;
  font-weight: 400;
  font-size:14px !important;
  text-transform: uppercase;
  vertical-align: middle;
  position: sticky !important;
}

.b-table-sticky-header >>> .b-table th::before,
.b-table-sticky-header >>> .b-table th::after {
  content: '';
  width: 100%;
  position: absolute;
  left: 0;
}

.b-table-sticky-header >>> .b-table th::before {
  border-top-width: 1px;
  border-top-color: #e9ecef;
  top: 0;
}

.b-table-sticky-header >>> .b-table th::after {
  border-bottom-width: 2px;
  border-bottom-color: #212529;
  bottom: 0;
}

.b-table-sticky-header >>> .b-table td.b-table-sticky-column::after {
  content: '';
  width: 1px;
  height: 100%;
  box-shadow: rgba(0, 0, 0, 0.15) 8px 0 8px 1px;
  position: absolute;
  top: 0;
  right: 0;
}

.b-table-sticky-header >>> .b-table td {
  color: inherit;
  padding: 0;
  font-size:14px !important;
}

.b-table-sticky-header >>> .b-table td a {
  color: inherit;
}
</style>
